import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { AlertCircle, Trash2, PlusCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "react-hot-toast";

export default function DikshaInfoForm() {

  // Simple Yup validation schema
  const validationSchema = Yup.object().shape({
    // Personal details
    fullName: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters"),

    gender: Yup.string()
      .required("Gender is required")
      .oneOf(["Male", "Female", "Other"], "Please select a valid gender"),

    age: Yup.number()
      .typeError("Age must be a number")
      .required("Age is required")
      .positive("Age must be positive")
      .integer("Age must be a whole number")
      .min(1, "Age must be at least 1")
      .max(120, "Age must be less than 120"),

    contact: Yup.string()
      .required("Contact number is required")
      .matches(/^[0-9]{10}$/, "Must be exactly 10 digits"),

    email: Yup.string().required("Email is required").email("Please enter a valid email"),

    // Address and optional fields
    address: Yup.string()
      .required("Address is required")
      .min(5, "Address must be at least 5 characters"),

    wishes: Yup.string()
      .required("Please share your wishes")
      .min(5, "Please write at least 5 characters"),

    personalStory: Yup.string(),

    dikshaStatus: Yup.string()
      .oneOf(["Yes", "No"], "Please select a valid option")
      .required("This field is required"),

    dikshaPlace: Yup.string().when("dikshaStatus", {
      is: "Yes",
      then: (schema) => schema.required("Diksha place is required"),
    }),

    // Dates and status
    maritalStatus: Yup.string()
      .required("Please select your marital status")
      .oneOf(["Married", "Unmarried"], "Please select a valid option"),

    dob: Yup.date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Date of birth is required")
      .max(new Date(), "Date of birth cannot be in the future"),

    anniversary: Yup.date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .when("maritalStatus", {
        is: "Married",
        then: (schema) =>
          schema
            .required("Anniversary date is required")
            .max(new Date(), "Anniversary date cannot be in the future"),
      }),

    familyMembers: Yup.array().of(
      Yup.object().shape({
        relation: Yup.string().required("Relation is required"),
        name: Yup.string()
          .required("Name is required")
          .trim()
          .max(50, "Name can't exceed 50 characters"),
        contact: Yup.string().trim().nullable(),
        age: Yup.number()
          .typeError("Age must be a number")
          .required("Age is required")
          .positive("Age must be positive")
          .integer("Age must be an integer")
          .min(0, "Age cannot be negative")
          .max(120, "Age must be less than 120"),
      })
    ),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      maritalStatus: "",
      gender: "",
      dikshaStatus: "",
      familyMembers: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "familyMembers",
  });

  const maritalStatus = watch("maritalStatus");
  const dikshaStatus = watch("dikshaStatus");



  const onSubmit = async (data, e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_URL}/api/diksha-info-form`,
        data
      );

      if (response.data.success) {
        toast.success("Seva form submitted successfully!");
        reset();
      } else {
        toast.error(response.data.message || "Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Seva form submission error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };



  const RequiredIndicator = () => (
    <span className="text-[#B82A1E] ml-1" aria-label="required">
      *
    </span>
  );



  return (
    <>
      <div
        id="aboutTop"
        className="relative h-[60vh] overflow-hidden flex items-center justify-center"
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />

        {/* Red Overlay - Semi-transparent */}
        <div className="absolute inset-0 bg-[#7B0000] z-10"></div>

        {/* Content */}
        <div className="relative z-20 text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-serif mb-4">Seva Form</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
            Fill out this form to participate in our activities.
          </p>
          <div className="mt-8 inline-block">
            <div className="h-0.5 w-20 bg-white/70 mx-auto"></div>
            <div className="h-0.5 w-12 bg-white/50 mx-auto mt-1"></div>
            <div className="h-0.5 w-6 bg-white/30 mx-auto mt-1"></div>
          </div>
        </div>

      </div>
      <div className="max-w-7xl mx-auto px-4 py-8 bg-[#fcf9f5]">
        <div className="container mx-auto  pb-16"></div>

        <div className="grid grid-cols-1 gap-8">
          {/* Profile Form Section */}
          <div className="bg-white rounded-xl shadow-md p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#B82A1E]/5 rounded-full -translate-y-12 translate-x-12 -z-0"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif text-[#5D4037]">Seva Form</h2>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Full Name <RequiredIndicator />
                    </label>
                    <input
                      type="text"
                      {...register("fullName")}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.fullName ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.fullName && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.fullName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Age <RequiredIndicator />
                    </label>
                    <input
                      type="number"
                      {...register("age")}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.age ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.age && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.age.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Gender <RequiredIndicator />
                    </label>
                    <select
                      {...register("gender")}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.gender ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" /> {errors.gender.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Contact Number <RequiredIndicator />
                    </label>
                    <input
                      type="tel"
                      {...register("contact")}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.contact ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.contact && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.contact.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Email <RequiredIndicator />
                    </label>
                    <input
                      type="email"
                      {...register("email")}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Are you already Dikshit? <RequiredIndicator />
                    </label>
                    <select
                      {...register("dikshaStatus")}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.dikshaStatus ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    {errors.dikshaStatus && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" /> {errors.dikshaStatus.message}
                      </p>
                    )}
                  </div>

                  {dikshaStatus === "Yes" && (
                    <div>
                      <label className="block text-sm font-medium text-[#5D4037] mb-1">
                        Diksha Place <RequiredIndicator />
                      </label>
                      <input
                        type="text"
                        {...register("dikshaPlace")}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                          errors.dikshaPlace ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {errors.dikshaPlace && (
                        <p className="text-red-500 text-sm flex items-center mt-1">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.dikshaPlace.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-[#5D4037] mb-1">
                    Address <RequiredIndicator />
                  </label>
                  <textarea
                    rows="3"
                    {...register("address")}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                      errors.address ? "border-red-500" : "border-gray-300"
                    }`}
                  ></textarea>
                  {errors.address && (
                    <p className="text-red-500 text-sm flex items-center mt-1">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.address.message}
                    </p>
                  )}
                </div>

                {/* Personal Wishes */}
                <div>
                  <label className="block text-sm font-medium text-[#5D4037] mb-1">
                    Personal Wishes <RequiredIndicator />
                  </label>
                  <textarea
                    rows="3"
                    {...register("wishes")}
                    placeholder="Share your wishes and aspirations..."
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                      errors.wishes ? "border-red-500" : "border-gray-300"
                    }`}
                  ></textarea>
                  {errors.wishes && (
                    <p className="text-red-500 text-sm flex items-center mt-1">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.wishes.message}
                    </p>
                  )}
                </div>

                {/* Personal Story */}
                <div>
                  <label className="block text-sm font-medium text-[#5D4037] mb-1">
                    Personal Story
                  </label>
                  <textarea
                    rows="5"
                    {...register("personalStory")}
                    placeholder="Share your personal journey..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E]"
                  ></textarea>
                </div>

                {/* Marital Status */}
                <div>
                  <label className="block text-sm font-medium text-[#5D4037] mb-1">
                    Marital Status <RequiredIndicator />
                  </label>
                  <select
                    {...register("maritalStatus")}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                      errors.maritalStatus ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="Unmarried">Unmarried</option>
                    <option value="Married">Married</option>
                  </select>
                  {errors.maritalStatus && (
                    <p className="text-red-500 text-sm flex items-center mt-1">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.maritalStatus.message}
                    </p>
                  )}
                </div>

                {/* DOB */}
                <div>
                  <label className="block text-sm font-medium text-[#5D4037] mb-1">
                    Date of Birth <RequiredIndicator />
                  </label>
                  <input
                    type="date"
                    {...register("dob", { valueAsDate: true })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                      errors.dob ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-sm flex items-center mt-1">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.dob.message}
                    </p>
                  )}
                </div>

                {maritalStatus === "Married" && (
                  <div>
                    <label className="block text-sm font-medium text-[#5D4037] mb-1">
                      Anniversary <RequiredIndicator />
                    </label>
                    <input
                      type="date"
                      {...register("anniversary", { valueAsDate: true })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                        errors.anniversary ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.anniversary && (
                      <p className="text-red-500 text-sm flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.anniversary.message}
                      </p>
                    )}
                  </div>
                )}

                {/* Family Members */}
                <div>
                  <label className="block text-sm font-medium text-[#5D4037] mb-2">
                    Family Members
                  </label>
                  <div className="space-y-4">
                    {fields.map((item, index) => (
                      <div
                        key={item.id}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start p-4 border rounded-lg bg-gray-50"
                      >
                        <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-[#5D4037] mb-1">
                              Name <RequiredIndicator />
                            </label>
                            <input
                              {...register(`familyMembers.${index}.name`)}
                              placeholder="Name"
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                                errors.familyMembers?.[index]?.name
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                            />
                            {errors.familyMembers?.[index]?.name && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.familyMembers[index].name.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#5D4037] mb-1">
                              Contact
                            </label>
                            <input
                              {...register(`familyMembers.${index}.contact`)}
                              placeholder="Contact (optional)"
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                                errors.familyMembers?.[index]?.contact
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                            />
                            {errors.familyMembers?.[index]?.contact && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.familyMembers[index].contact.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="md:col-span-1">
                          <label className="block text-sm font-medium text-[#5D4037] mb-1">
                            Relation <RequiredIndicator />
                          </label>
                          <input
                            {...register(`familyMembers.${index}.relation`)}
                            placeholder="Relation (e.g., Spouse)"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                              errors.familyMembers?.[index]?.relation
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {errors.familyMembers?.[index]?.relation && (
                            <p className="text-red-500 text-sm mt-1">Relation is required</p>
                          )}
                        </div>
                        <div className="md:col-span-1">
                          <label className="block text-sm font-medium text-[#5D4037] mb-1">
                            Age <RequiredIndicator />
                          </label>
                          <input
                            type="number"
                            {...register(`familyMembers.${index}.age`)}
                            placeholder="Age"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#B82A1E]/30 focus:border-[#B82A1E] ${
                              errors.familyMembers?.[index]?.age
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {errors.familyMembers?.[index]?.age && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.familyMembers[index].age.message}
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => append({ name: "", relation: "", age: "", contact: "" })}
                    className="mt-4 px-4 py-2 rounded-lg bg-[#B82A1E]/10 text-[#B82A1E] hover:bg-[#B82A1E]/20 transition-colors flex items-center gap-2"
                  >
                    <PlusCircle size={16} />
                    <span>Add Family Member</span>
                  </button>
                </div>



                {/* Form actions */}
                <div className="flex justify-end gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || !isDirty}
                    className="px-6 py-3 rounded-lg bg-[#B82A1E] hover:bg-[#9a231a] text-white transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      "Submit Seva Form"
                    )}
                  </button>
                </div>
              </form>
            </div>

            <div className="absolute bottom-4 right-4 text-[#B82A1E]/10 text-6xl font-serif z-0">
              ॐ
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
